import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { auth, db } from "../../firebase/firebaseConfig";
import { collection, doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { Link, useNavigate } from "react-router-dom";
import Modal from "../../components/ui/Modal";
import routePaths from "../../routes/routePaths";
import toast from "react-hot-toast";
import { FiUserPlus } from "react-icons/fi";

const VN_PHONE_REGEX = /^(?:0|\+84)(?:\d{9})$/;

function splitDisplayName(name = "") {
  const parts = name.trim().split(/\s+/);
  const firstName = parts[0] || "";
  const lastName = parts.slice(1).join(" ") || "";
  return { firstName, lastName };
}

async function ensureUserDoc(user) {
  const ref = doc(db, "users", user.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    const { firstName, lastName } = splitDisplayName(user.displayName || "");
    await setDoc(
      ref,
      {
        uid: user.uid,
        email: user.email || "",
        firstName,
        lastName,
        photoData: user.photoURL || "",
        createdAt: serverTimestamp(),
        role: "student",
        isVerified: !!user.emailVerified,
        isBlocked: false,
        isDelete: false,
      },
      { merge: true }
    );
  }
}

const UserRegisterPage = () => {
  const { t } = useTranslation();
  const [modal, setModal] = useState({ open: false, title: "", message: "" });
  const [emailError, setEmailError] = useState("");
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
      phoneNumber: "",
      address: "",
      gender: "",
      birthday: ""
    }
  });

  // Xử lý kết quả Google Redirect (nếu popup bị chặn)
  useEffect(() => {
    (async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user) {
          await ensureUserDoc(result.user);
          toast.success(t("userRegister.googleSuccess"));
          navigate(routePaths.profile);
        }
      } catch (error) {
        if (error && (error.message || error.code)) {
          toast.error(`${t("userRegister.googleError")}: ${error.message || error.code}`);
        }
      }
    })();
  }, [navigate, t]);

  const handleGoogleRegister = async () => {
    setEmailError("");
    setLoadingGoogle(true);
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      await ensureUserDoc(result.user);
      toast.success(t("userRegister.googleSuccess"));
      navigate(routePaths.profile);
    } catch (error) {
      // Thử fallback redirect với các lỗi popup phổ biến
      if (
        error?.code === "auth/popup-blocked" ||
        error?.code === "auth/cancelled-popup-request" ||
        error?.code === "auth/popup-closed-by-user"
      ) {
        try {
          await signInWithRedirect(auth, provider);
          return;
        } catch {
          toast.error(t("userRegister.googleBlocked"));
        }
      } else {
        toast.error(`${t("userRegister.googleError")}: ${error?.message || error?.code || "Unknown error"}`);
        if (error?.code === "auth/email-already-in-use") {
          setEmailError(t("userRegister.emailInUse"));
        }
      }
    } finally {
      setLoadingGoogle(false);
    }
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    setEmailError("");
    try {
      const { email, password, firstName, lastName, ...rest } = data;
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const user = cred.user;

      await updateProfile(user, { displayName: `${firstName} ${lastName}`.trim() });

      await setDoc(doc(collection(db, "users"), user.uid), {
        uid: user.uid,
        email: user.email || "",
        firstName,
        lastName,
        ...rest,
        createdAt: serverTimestamp(),
        role: "student",
        isVerified: false,
        isBlocked: false,
        isDelete: false,
        photoData: ""
      }, { merge: true });

      await sendEmailVerification(user);

      setModal({
        open: true,
        title: t("userRegister.title"),
        message: t("userRegister.successMessage")
      });
    } catch (error) {
      if (error?.code === "auth/email-already-in-use") {
        setEmailError(t("userRegister.emailInUse"));
      } else {
        toast.error(t("userRegister.errorMessage"));
        setModal({
          open: true,
          title: t("userRegister.title"),
          message: t("userRegister.errorMessage")
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setModal((m) => ({ ...m, open: false }));
    if (modal.message === t("userRegister.successMessage")) {
      navigate(routePaths.profile);
    }
  };

  const password = watch("password");

  return (
    <div className="max-w-xl mx-auto px-4 py-10">
      <div className="bg-card shadow-lg rounded-2xl p-8 border border-border">
        <div className="text-center mb-7">
          <img src="/default-avatar.png" alt="avatar" className="w-20 h-20 mx-auto rounded-full mb-2 border-2 border-border shadow" />
          <h2 className="text-2xl font-bold text-primary">{t("userRegister.title")}</h2>
          <p className="text-sm text-menu">{t("userRegister.subtitle")}</p>
        </div>

        {/* Google Button */}
        <button
          type="button"
          onClick={handleGoogleRegister}
          disabled={loadingGoogle || submitting}
          className="w-full flex items-center justify-center gap-2 border border-border bg-card hover:bg-card/80 text-primary font-semibold rounded-lg py-2 mb-6 shadow transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <img src="/google-icon.svg" alt="Google" className="w-5 h-5" />
          {loadingGoogle ? t("userRegister.googleLoading") : t("userRegister.googleRegisterButton")}
        </button>

        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-card px-2 text-menu">{t("userRegister.or")}</span>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-semibold text-menu">{t("userRegister.firstName")}</label>
              <input
                {...register("firstName", { required: true, minLength: 1 })}
                placeholder={t("userRegister.firstName")}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-primary text-primary"
              />
              {errors.firstName && <p className="text-error text-xs mt-1">{t("userRegister.firstNameRequired")}</p>}
            </div>
            <div>
              <label className="font-semibold text-menu">{t("userRegister.lastName")}</label>
              <input
                {...register("lastName", { required: true, minLength: 1 })}
                placeholder={t("userRegister.lastName")}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-primary text-primary"
              />
              {errors.lastName && <p className="text-error text-xs mt-1">{t("userRegister.lastNameRequired")}</p>}
            </div>
          </div>

          <div>
            <label className="font-semibold text-menu">{t("userRegister.email")}</label>
            <input
              type="email"
              {...register("email", {
                required: true,
                pattern: /^\S+@\S+$/i
              })}
              placeholder="your@email.com"
              className={`w-full border border-border rounded-lg px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-primary text-primary ${emailError ? 'border-error' : ''}`}
            />
            {errors.email?.type === "required" && <p className="text-error text-xs mt-1">{t("userRegister.emailRequired")}</p>}
            {errors.email?.type === "pattern" && <p className="text-error text-xs mt-1">{t("userRegister.emailInvalid")}</p>}
            {emailError && (
              <p className="text-error text-xs mt-1">
                {emailError} <Link to={routePaths.login} className="underline text-primary">{t("userRegister.loginInstead")}</Link>
              </p>
            )}
          </div>

          <div>
            <label className="font-semibold text-menu">{t("userRegister.password")}</label>
            <input
              type="password"
              {...register("password", { required: true, minLength: 6 })}
              placeholder="********"
              className="w-full border border-border rounded-lg px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-primary text-primary"
            />
            {errors.password?.type === "required" && <p className="text-error text-xs mt-1">{t("userRegister.passwordRequired")}</p>}
            {errors.password?.type === "minLength" && <p className="text-error text-xs mt-1">{t("userRegister.passwordMin", { n: 6 })}</p>}
          </div>

          <div>
            <label className="font-semibold text-menu">{t("userRegister.confirmPassword")}</label>
            <input
              type="password"
              {...register("confirmPassword", {
                required: true,
                validate: (value) => value === password
              })}
              placeholder={t("userRegister.confirmPassword")}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-primary text-primary"
            />
            {errors.confirmPassword?.type === "required" && <p className="text-error text-xs mt-1">{t("userRegister.confirmPasswordRequired")}</p>}
            {errors.confirmPassword?.type === "validate" && <p className="text-error text-xs mt-1">{t("userRegister.passwordsNotMatch")}</p>}
          </div>

          <div>
            <label className="font-semibold text-menu">{t("userRegister.phoneNumber")}</label>
            <input
              {...register("phoneNumber", {
                pattern: VN_PHONE_REGEX
              })}
              placeholder="0xxxxxxxxx"
              className="w-full border border-border rounded-lg px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-primary text-primary"
            />
            {errors.phoneNumber && <p className="text-error text-xs mt-1">{t("userRegister.phoneInvalid")}</p>}
          </div>

          <div>
            <label className="font-semibold text-menu">{t("userRegister.address")}</label>
            <input
              {...register("address")}
              placeholder={t("userRegister.address")}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-primary text-primary"
            />
          </div>

          <div>
            <label className="font-semibold text-menu">{t("userRegister.gender")}</label>
            <select
              {...register("gender")}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-primary text-primary"
            >
              <option value="">{t("userRegister.chooseGender")}</option>
              <option value="male">{t("userRegister.male")}</option>
              <option value="female">{t("userRegister.female")}</option>
              <option value="other">{t("userRegister.other")}</option>
            </select>
          </div>

          <div>
            <label className="font-semibold text-menu">{t("userRegister.birthday")}</label>
            <input
              type="date"
              {...register("birthday")}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm mt-1 focus:ring-2 focus:ring-primary text-primary"
            />
          </div>

          <button
            type="submit"
            disabled={submitting || loadingGoogle}
            className="w-full flex justify-center items-center gap-2 bg-primary text-textButton py-2 rounded-lg hover:bg-primary/90 font-semibold text-base shadow mt-2 transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FiUserPlus className="text-xl" />
            {t("userRegister.registerButton")}
          </button>

          <p className="text-center text-sm mt-4 text-menu">
            {t("userRegister.alreadyHaveAccount")}{" "}
            <Link to={routePaths.login} className="text-primary hover:underline">
              {t("userRegister.loginNow")}
            </Link>
          </p>
        </form>
      </div>

      <Modal
        isOpen={modal.open}
        title={modal.title}
        message={modal.message}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default UserRegisterPage;
